import cn from 'clsx';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useScrollPositionStore } from '@pkgs/components/hooks/useScrollPositionStore';
import { useLayoutAnimationHorizontalDirection } from '@pkgs/components/hooks/useLayoutAnimationHorizontalDirection';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSelectedState } from '@pkgs/components/hooks/stores/search/useSelectedState';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { isPolygonDrawOrEditMapMode } from '@pkgs/components/hooks/stores/search/slices/mapInteractionSlice';
import { MarketCountryCode } from '@ev-tech/search-modules-api';

import { Visibility } from '../Visibility/Visibility';
import { SearchModeToggle } from '../SearchModeToggle/SearchModeToggle';
import { SearchAlertButton } from '../SearchAlert/SearchAlertButton';
import { ObjectTypeFilterButton } from '../ObjectTypeFilter/ObjectTypeFilterButton';
import { LocationFilter } from '../LocationFilter/LocationFilter';
import { LifestyleFilterButton } from '../LifestyleFilter/LifestyleFilterButton';
import { SurfaceFilterDropdown } from '../FilterDropdowns/SurfaceFilterDropdown';
import { PriceFilterDropdown } from '../FilterDropdowns/PriceFilterDropdown';
import { ConstructionFilterDropdown } from '../FilterDropdowns/ConstructionFilterDropdown';
import { BuyRentFilter } from '../BuyRentFilter/BuyRentFilter';
import { AdvancedFiltersDrawer } from '../AdvancedFilters/AdvancedFiltersDrawer';

import styles from './FilterBar.module.css';

type FilterBarProps = {
  className?: string;
};

export const FilterBar = ({ className }: FilterBarProps) => {
  const { countryCode, featureFlags } = useAppContext();
  const isLipaPage = useIsLipaPage();
  const { isDevelopmentSelected } = useSelectedState();
  const isHeaderVisible = useScrollPositionStore((state) => state.isHeaderVisible);
  const isPrivateOffice = countryCode === MarketCountryCode['private-office'];
  const searchMode = useSearchStore((state) => state.searchMode);

  const { breakpoint = 0 } = useBreakpoint();
  const shouldAddWrapperRef = featureFlags.isAiSearchEnabled && breakpoint && breakpoint > Breakpoints.TABLET_PORTRAIT;

  const isMapDrawOrEditMode = useSearchStore((state) => isPolygonDrawOrEditMapMode(state.mapMode));

  // Track dependencies that affect the button's position
  const wrapperRef = useLayoutAnimationHorizontalDirection<HTMLDivElement>([
    featureFlags.isAiSearchEnabled,
    searchMode,
    isPrivateOffice,
    isDevelopmentSelected,
    isLipaPage,
  ]);

  return (
    <div
      data-testid="search-components_filter-bar"
      className={cn(
        styles.filterBarContainer,
        isHeaderVisible && styles.headerVisible,
        isMapDrawOrEditMode && styles.mapDrawOrEditMode,
        className,
      )}
    >
      {featureFlags.isAiSearchEnabled && (
        <Visibility visibleFrom="tabletLandscape">
          <SearchModeToggle />
        </Visibility>
      )}
      {searchMode === SearchMode.CLASSIC && !isPrivateOffice && (
        <Visibility visibleFrom="tabletLandscape">
          <div className={styles.filterWrapper}>
            <BuyRentFilter hasLabelHidden={true} />
          </div>
        </Visibility>
      )}
      <LocationFilter searchMode={searchMode} />
      {searchMode === SearchMode.CLASSIC && (
        <div className={styles.filterWrapper}>
          <Visibility visibleFrom="laptop">
            <ObjectTypeFilterButton />
          </Visibility>
          {isPrivateOffice && (
            <Visibility visibleFrom="laptop">
              <LifestyleFilterButton />
            </Visibility>
          )}
          {isDevelopmentSelected ? (
            <Visibility visibleFrom="laptop">
              <ConstructionFilterDropdown data-testid="search-components_filter-bar_construction-year-filter" />
            </Visibility>
          ) : (
            <>
              <Visibility visibleFrom="laptop">
                <PriceFilterDropdown data-testid="search-components_filter-bar_price-filter" />
              </Visibility>
              {!featureFlags.isAiSearchEnabled && (
                <Visibility visibleFrom="laptop">
                  <SurfaceFilterDropdown data-testid="search-components_filter-bar_surface-filter" />
                </Visibility>
              )}
            </>
          )}
          <AdvancedFiltersDrawer className={className} variant="filter-bar" />
        </div>
      )}
      {breakpoint > Breakpoints.TABLET_PORTRAIT && (
        <>
          <div className={styles.divider} />
          <div
            ref={shouldAddWrapperRef ? wrapperRef : null}
            className={cn(styles.searchAlertButtonWrapper, featureFlags.isAiSearchEnabled && styles.aiSearchEnabled)}
          >
            <SearchAlertButton data-testid="search-components_filter-bar_search-alert-button" context="filter-bar" />
          </div>
        </>
      )}
    </div>
  );
};
